import React from 'react';
import { components } from 'react-select';
import { Flex } from '@chakra-ui/react';
import { ChevronRight } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components';
import { DepartmentTreePopover } from './DepartmentTreePopover';
import {
  type DepartmentNode,
  collapseDepartmentPath,
  getDepartmentPath,
} from './departmentPath';

const DEPARTMENT_NAME_MAX_WIDTH = '200px';

/**
 * The department's breadcrumb: up to three levels in full, deeper paths as
 * root › … › parent › department with the full path on hover. Shared by the
 * select's value and read-only displays (DepartmentPath) so both look alike.
 */
export const DepartmentPathTrail = ({
  department,
  allDepartments,
  labelKey = 'name',
  wrap = false,
}: {
  department: DepartmentNode;
  allDepartments: DepartmentNode[];
  labelKey?: string;
  wrap?: boolean;
}) => {
  const displayPath = collapseDepartmentPath(
    getDepartmentPath(department, allDepartments)
  );

  return (
    <Flex alignItems="center" gap="1" flexWrap={wrap ? 'wrap' : 'nowrap'}>
      {displayPath.map((item, index) => (
        <React.Fragment key={item.id}>
          {index > 0 && <ChevronRight color="secondary-700" size={20} />}
          {item.id === 'ellipsis' ? (
            <DepartmentTreePopover
              department={department}
              allDepartments={allDepartments}
            >
              <KText
                color="secondary-700"
                fontSize="sm"
                fontWeight="medium"
                cursor="pointer"
              >
                {item[labelKey]}
              </KText>
            </DepartmentTreePopover>
          ) : (
            // A very long name is cut short, with the whole name on hover, so
            // one department can't stretch its row over several lines.
            <KText
              color={index === 0 ? 'secondary-900' : 'secondary-700'}
              fontSize="sm"
              maxW={DEPARTMENT_NAME_MAX_WIDTH}
              isTruncated
              enableTooltip
            >
              {item.name}
            </KText>
          )}
        </React.Fragment>
      ))}
    </Flex>
  );
};

export const DepartmentSingleValue = (props: any) => {
  const { data, selectProps, labelKey = 'name' } = props;

  return (
    <components.SingleValue {...props}>
      <DepartmentPathTrail
        department={data}
        allDepartments={selectProps.options}
        labelKey={labelKey}
      />
    </components.SingleValue>
  );
};

// for handling multi-select values
export const DepartmentMultiValue = (props: any) => {
  const { data, selectProps, labelKey = 'name' } = props;
  const { options } = selectProps;
  const nodePath = getDepartmentPath(data, options);

  return (
    <components.MultiValue {...props}>
      <DepartmentTreePopover department={data} allDepartments={options}>
        <KText
          color="secondary-700"
          fontSize="sm"
          title={nodePath.map((item) => item.name).join(' > ')}
        >
          {data[labelKey]}
        </KText>
      </DepartmentTreePopover>
    </components.MultiValue>
  );
};
